import { Component, ReactNode, ErrorInfo } from "react";
import { AlertTriangle } from "lucide-react";
import { captureException } from "@/services/sentry";
import { Button } from "@/components/ui/button";

interface FallbackUIProps {
  error?: string;
}

/**
 * Fallback UI when the production error boundary catches a render failure.
 */
export function FallbackUI({ error }: Readonly<FallbackUIProps>) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div
        className="text-center p-8 max-w-md mx-auto rounded-xl border border-border bg-card shadow-sm space-y-4"
        role="alert"
      >
        <div className="flex justify-center">
          <AlertTriangle className="h-10 w-10 text-destructive" aria-hidden />
        </div>
        <div className="space-y-2">
          <h1 className="text-xl font-semibold text-foreground">Something went wrong</h1>
          <p className="text-sm text-foreground/75">
            TellerWand hit an unexpected error. Reload the page to try again.
          </p>
          {error ? (
            <p className="text-xs text-foreground/60 font-mono break-words">{error}</p>
          ) : null}
        </div>
        <Button type="button" onClick={() => window.location.reload()}>
          Reload page
        </Button>
      </div>
    </div>
  );
}

// Minimal error boundary for production fallback
export class ProductionErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  /**
   * Component Did Catch.
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Production Error Boundary caught an error:", error, errorInfo);

    try {
      captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
        },
        tags: {
          errorBoundary: "ProductionErrorBoundary",
        },
      });
    } catch (sentryError) {
      console.warn("Failed to capture error in Sentry:", sentryError);
    }

    if (window.OpenReplay && typeof window.OpenReplay.issue === "function") {
      try {
        window.OpenReplay.issue("Production Error Boundary", {
          error: error.message,
          stack: error.stack,
          componentStack: errorInfo.componentStack,
          context: "production_error_boundary",
        });
      } catch (openReplayError) {
        console.warn("Failed to track error in OpenReplay:", openReplayError);
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return <FallbackUI error={this.state.error?.message} />;
    }

    return this.props.children;
  }
}
